import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CONSENT_POLICY_VERSION,
  ConsentCategory,
  ConsentRegime,
  ConsentState,
  allDenied,
  allGranted,
  consentNeedsPrompt,
  impliedConsentState,
  storedToConsentState,
} from "@shared/consent";
import { api } from "../api";
import { resolveRegime } from "./geo";
import { consentBootstrappedInHtml, pushConsentToGTM } from "./gtm";
import { readStoredConsent, writeConsentCookie } from "./store";
import { applyConsentToVendors } from "./vendors";

/**
 * Owns the consent decision for the whole app.
 *
 * The flow it implements:
 *
 *   1. A stored choice, if there is one, is already live before React mounts —
 *      the inline snippet in index.html applied it to Consent Mode, and
 *      main.tsx read the same cookie to decide how to initialise PostHog. This
 *      provider only confirms it and applies it to the vendors it owns.
 *
 *   2. Otherwise Cloudflare tells us where the visitor is. In the EEA, the UK
 *      and Switzerland nothing non-essential runs until they agree, and the
 *      banner is shown. Everywhere else the default is granted and withdrawable,
 *      so it is applied and persisted without interrupting anyone.
 *
 * Cloudflare's answer is treated as authoritative over the region defaults
 * declared in the HTML, which come from Google's own geolocation: on the rare
 * occasion the two disagree, an explicit update lands right after resolution
 * and corrects it.
 */

export type ConsentDecisionSource = "accept_all" | "reject_all" | "preferences";

type ConsentContextValue = {
  state: ConsentState;
  regime: ConsentRegime;
  country: string | null;
  /** False until the regime is known; the banner must not flash before then. */
  resolved: boolean;
  /** An answer is required and has not been given. */
  needsDecision: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (choices: Record<Exclude<ConsentCategory, "necessary">, boolean>) => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

function logConsentDecision(
  state: ConsentState,
  regime: ConsentRegime,
  country: string | null,
  source: ConsentDecisionSource
): void {
  // Proof that consent was given is the one thing a self-hosted banner has to
  // keep that a SaaS kept for us. Fire-and-forget: a failed log must never stop
  // the user's choice being applied.
  api
    .post("/consent", {
      categories: {
        functional: state.functional,
        analytics: state.analytics,
        marketing: state.marketing,
      },
      regime,
      country,
      source,
      policyVersion: CONSENT_POLICY_VERSION,
      language: document.documentElement.lang || null,
      pageUrl: window.location.href,
    })
    .catch(() => {
      /* logged server-side on the next successful call; nothing to do here */
    });
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const stored = useRef(readStoredConsent()).current;

  const [state, setState] = useState<ConsentState>(() =>
    stored ? storedToConsentState(stored) : allDenied()
  );
  const [regime, setRegime] = useState<ConsentRegime>(stored?.r ?? "opt-in");
  const [country, setCountry] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [needsDecision, setNeedsDecision] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const geo = await resolveRegime();
      if (cancelled) return;

      setCountry(geo.country);

      // A stored answer the user actually gave stands. A stored *implied* grant
      // does not travel: someone whose first visit was from an opt-out region
      // and whose next is from the EEA has to be asked, so the grant is dropped
      // and treated as though nothing were stored.
      const mustPrompt = consentNeedsPrompt(stored, geo.regime);
      const honoured = stored && !mustPrompt ? stored : null;

      setRegime(honoured?.r ?? geo.regime);

      if (honoured) {
        const storedState = storedToConsentState(honoured);
        applyConsentToVendors(storedState);
        // The HTML snippet already pushed this exact state before GTM loaded;
        // repeating it would re-fire every container trigger on the event. It
        // skips implied grants, which is why this only holds for honoured ones.
        if (!consentBootstrappedInHtml()) {
          pushConsentToGTM(storedState, honoured.r, geo.country, "stored");
        }
        setResolved(true);
        return;
      }

      // In an opt-out region the default is a grant, except where the browser
      // has already said no on the user's behalf via Global Privacy Control.
      const implied = impliedConsentState(geo.regime, geo.gpc);
      setState(implied);
      if (geo.regime === "opt-out") {
        // Persisting the implied grant saves re-deriving it every load. It is
        // tagged `implied` so neither the inline snippet nor a later visit from
        // an opt-in region can mistake it for an answer.
        writeConsentCookie(implied, geo.regime, "implied");
      }

      applyConsentToVendors(implied);
      pushConsentToGTM(implied, geo.regime, geo.country, "implied");
      setNeedsDecision(geo.regime === "opt-in");
      setResolved(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [stored]);

  const commit = useCallback(
    (next: ConsentState, source: ConsentDecisionSource) => {
      setState(next);
      writeConsentCookie(next, regime, "user");
      applyConsentToVendors(next);
      pushConsentToGTM(next, regime, country, "user");
      logConsentDecision(next, regime, country, source);
      setNeedsDecision(false);
      setPreferencesOpen(false);
    },
    [country, regime]
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      regime,
      country,
      resolved,
      needsDecision,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      acceptAll: () => commit(allGranted(), "accept_all"),
      rejectAll: () => commit(allDenied(), "reject_all"),
      savePreferences: (choices) =>
        commit({ necessary: true, ...choices }, "preferences"),
    }),
    [commit, country, needsDecision, preferencesOpen, regime, resolved, state]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}

/**
 * The preference centre opener for surfaces that only need that one action —
 * footers, the settings screen. Safe to call from a component rendered outside
 * the provider, where it simply does nothing.
 */
export function useOpenCookiePreferences(): () => void {
  const ctx = useContext(ConsentContext);
  return useCallback(() => ctx?.openPreferences(), [ctx]);
}

/**
 * The regime in force, or null while it is still being resolved or when called
 * from outside the provider.
 *
 * Callers use this to label rather than to gate, so "not yet known" has to be
 * distinguishable from "opt-out": rendering the US wording for a moment and
 * then swapping it would be worse than waiting a beat.
 */
export function useResolvedRegime(): ConsentRegime | null {
  const ctx = useContext(ConsentContext);
  return ctx?.resolved ? ctx.regime : null;
}
